import { httpClient } from "../../../shared/api/httpClient";
import type {
  CursorPaginatedResponse,
  PaginatedResponse,
} from "../../../shared/api/pagination";
import type {
  CreateOrderRequest,
  OrderLeadTimeValidationRequest,
  OrderLeadTimeValidationResponse,
  OrderEditorPayload,
  OrderResponse,
  UpdateOrderRequest,
} from "../model/types";

const toOrderRequest = (payload: OrderEditorPayload): CreateOrderRequest => ({
  group_id: payload.group_id,
  customer_code: payload.customer_code,
  branch_code: payload.branch_code || "",
  line_id: payload.line_id == null ? null : String(payload.line_id),
  type: payload.type,
  status: payload.status || "pending",
  sales_date: payload.sales_date || "",
  total_amount: payload.total_amount,
  notes: payload.notes,
  order_items: payload.order_items.map((item) => ({
    product_code: item.product_code || "",
    product_name: item.product_name,
    origin_code: item.origin_code,
    origin_name: item.origin_name,
    shipper_code: item.shipper_code,
    shipper_name: item.shipper_name,
    grade: item.grade,
    rank: item.rank,
    order_unit: item.order_unit,
    order_quantity: item.order_quantity || 0,
    weight: item.weight,
    package_type: item.package_type,
    quantity_per_package: item.quantity_per_package,
    proposed_price: item.proposed_price,
    line_total: item.line_total,
    notes: item.notes,
    daily_note: item.daily_note,
    weekly_note: item.weekly_note,
  })),
});

export const fetchOrders = async (
  page = 1,
  pageSize = 20,
  search?: string,
  status?: string,
): Promise<PaginatedResponse<OrderResponse>> => {
  const response = await httpClient.get<PaginatedResponse<OrderResponse>>(
    "/orders",
    {
      params: {
        page,
        page_size: pageSize,
        search: search?.trim() || undefined,
        status: status || undefined,
      },
    },
  );
  return response.data;
};

export const fetchOrder = async (orderId: number): Promise<OrderResponse> => {
  const response = await httpClient.get<OrderResponse>(`/orders/${orderId}`);
  return response.data;
};

export const fetchOrderUnitOptions = async ({
  search,
  cursor,
  limit = 20,
}: { search?: string; cursor?: string; limit?: number } = {}): Promise<
  CursorPaginatedResponse<string>
> => {
  const response = await httpClient.get<CursorPaginatedResponse<string>>(
    "/orders/item-options/units",
    {
      params: {
        search: search?.trim() || undefined,
        cursor: cursor || undefined,
        limit,
      },
    },
  );
  return response.data;
};

export const validateOrderLeadTime = async (
  payload: OrderLeadTimeValidationRequest,
): Promise<OrderLeadTimeValidationResponse> => {
  const response = await httpClient.post<OrderLeadTimeValidationResponse>(
    "/orders/lead-time-validation",
    payload,
  );
  return response.data;
};

export const createOrder = async (
  payload: OrderEditorPayload,
): Promise<OrderResponse> => {
  const response = await httpClient.post<OrderResponse>(
    "/orders",
    toOrderRequest(payload),
  );
  return response.data;
};

export const updateOrder = async (
  orderId: number,
  payload: OrderEditorPayload,
): Promise<OrderResponse> => {
  const request: UpdateOrderRequest = toOrderRequest(payload);
  const response = await httpClient.put<OrderResponse>(
    `/orders/${orderId}`,
    request,
  );
  return response.data;
};
